import { useState } from "react";
import styled from "styled-components";
import Spacer from "../Spacer";
import plus from "assets/icons/plus_secondary.svg";
import ImgWrapper from "../ImgWrapper";
import { useClickOutside } from "webrix/hooks";
import { useHistory } from "react-router-dom";
import Button from "components/global/Button";

const Wrapper = styled.div`
  width: 240px;
  max-width: 240px;
  max-height: 202px;
  overflow-y: auto;
  overflow-x: hidden;
  position: absolute;
  left: ${({ top }) => (top ? "12px" : "228px")};
  top: ${({ top }) => (top ? "" : "unset")};
  bottom: ${({ top }) => (top ? "unset" : "32px")};
  z-index: 20;
  background: #fff;
  box-shadow: var(--shadow);
  border-radius: 8px;
  color: var(--grey_1);

  .title {
    font-weight: 450;
    font-size: 12px;
    color: var(--body_text);
    padding: 0 12px;
  }

  .list {
    padding: 0 12px;
  }

  .listItem {
    padding: 13px 15px;
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 9px;
    border-radius: 8px;
    cursor: pointer;

    &.selected {
      background: var(--grey_10);
      pointer-events: none;
    }

    &:hover {
      background: var(--background);
    }

    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .check-container {
    width: 20px;
  }

  .name-container {
    display: flex;
    gap: 7px;
    align-items: center;
  }

  .imgWrapper {
    color: #fff;
  }

  .addWorkspaceWrapper {
    padding-inline: 12px;

    .noBorder {
      padding: 0;

      &:hover {
        background-color: transparent;
        color: var(--secondary);
      }
    }
  }

  @media screen and (max-width: 768px) {
    left: ${({ top }) => (top ? "24px" : "40%")};
  }
`;

/**
 * DropdownList component.
 *
 * @component
 * @param {object} props
 * @param {string} props.type - The type of the dropdown list.
 * @param {string} props.title - The title of the dropdown list.
 * @param {function} props.setShowDropdownList - The function to set the visibility of the dropdown list.
 * @param {boolean} [props.top] - Indicates whether the dropdown list should be positioned at the top.
 * @param {Array} props.list - The list of items to display in the dropdown list.
 * @param {any} [props.lastListElementRef] - The ref of the last list element.
 * @param {object} [props.defaultSelected] - The default selected item.
 * @param {function} [props.callback] - The callback function to be called when an item is clicked.
 * @param {object} [props.user] - The user object.
 * @param {Array} [props.user_accounts] - The list of user accounts.
 * @param {boolean} [props.isLoading] - Indicates whether the dropdown list is in a loading state.
 * @returns {JSX.Element} The DropdownList component.
 */
const DropdownList = ({
  type,
  title,
  setShowDropdownList,
  top,
  list,
  lastListElementRef,
  defaultSelected,
  callback,
  user,
  user_accounts,
  isLoading,
}) => {
  const history = useHistory();
  const [selected, setSelected] = useState(defaultSelected);
  const handleOnMouseDownCapture = useClickOutside(() => {
    setShowDropdownList(false);
  });

  const handleClick = (item) => {
    setSelected(item);
    callback(item);
    setShowDropdownList(false);
  };

  const userHasWorkspace = user_accounts?.find(
    (account) => account.owner.id === user.id
  );

  return (
    <Wrapper
      onMouseDownCapture={handleOnMouseDownCapture}
      top={top}
      className="sidebar-dropdown-tour"
    >
      {title && <header className="title">{title}</header>}
      <Spacer y={1.2} />

      <ul className="list">
        {list.map((item, idx) => (
          <li
            className={`listItem ${
              type === "brand" && item.status !== 1 && "disabled"
            }${selected?.id === item.id ? " selected" : ""}`}
            onClick={() => handleClick(item)}
            key={item.id}
            ref={list.length === idx + 1 ? lastListElementRef : null}
          >
            <div className="name-container">
              <ImgWrapper
                size={2.4}
                imgHeight={"100%"}
                className="imgWrapper"
                contain
                bg={item?.avatar ? "inherit" : "var(--primary)"}
                borderColor="var(--grey_10)"
              >
                {item.avatar ? (
                  <img src={item?.avatar} alt="brand" />
                ) : (
                  <span className="colorWhite textSmall upperCase">
                    {item.name?.charAt(0)}
                    {item.name?.charAt(1)}
                  </span>
                )}
              </ImgWrapper>
              <p className="item-name">{item.name}</p>
            </div>
          </li>
        ))}
      </ul>

      {!userHasWorkspace && title === "List of Workspaces" && (
        <div className="addWorkspaceWrapper">
          <Button
            text="Add my workspace"
            icon={plus}
            className="noBorder"
            color="var(--secondary)"
            onClick={() => history.push("/onboarding/create-workspace")}
          />
        </div>
      )}
    </Wrapper>
  );
};

export default DropdownList;
